/**
 * Speed Role - Validate Pillar
 *
 * Validação semântica de valores de velocidade.
 * Zero dependências externas - pode ser usado standalone.
 *
 * Nota: Velocidade pode ser negativa (movimento em direção oposta).
 *
 * @example
 * import { validateSpeed, isValidSpeed } from '@attrx/role-morphic/speed/validate';
 *
 * validateSpeed(100);   // { valid: true, errors: [] }
 * validateSpeed(-50);   // { valid: true, errors: [] } (negativo é válido)
 * isValidSpeed(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface SpeedValidationConfig {
  min?: number;
  max?: number;
  integer?: boolean;
  minError?: string;
  maxError?: string;
}

// =============================================================================
// CONFIG
// =============================================================================

/**
 * Configuração padrão de validação para velocidade
 * Nota: sem min/max por padrão - velocidade pode ser qualquer valor finito
 */
export const SPEED_VALIDATION_CONFIG: SpeedValidationConfig = {};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de velocidade
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateSpeed(100);              // { valid: true, errors: [] }
 * validateSpeed(-50);              // { valid: true, errors: [] }
 * validateSpeed(150, { max: 120 }) // { valid: false, errors: ['Value must be at most 120'] }
 */
export function validateSpeed(
  value: unknown,
  config: SpeedValidationConfig = SPEED_VALIDATION_CONFIG
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Min check
  if (config.min !== undefined && value < config.min) {
    const msg = config.minError ?? `Value must be at least ${config.min}`;
    errors.push(msg);
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  // Integer check
  if (config.integer && !Number.isInteger(value)) {
    errors.push("Value must be an integer");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor de velocidade é válido
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidSpeed(100);   // true
 * isValidSpeed(-50);   // true (negativo é válido)
 */
export function isValidSpeed(
  value: unknown,
  config: SpeedValidationConfig = SPEED_VALIDATION_CONFIG
): boolean {
  return validateSpeed(value, config).valid;
}
